<template>
  <VueCropper
    ref="cropper"
    :src="imageUrl"
    :view-mode="1"
    drag-mode="move"
    :aspect-ratio="aspectRatio"
    :guides="false"
    :background="false"
    :img-style="imgStyle"
    :min-container-width="minContainerWidth"
    :min-container-height="minContainerHeight"
    :crop-box-movable="false"
    :crop-box-resizable="false"
    :toggle-drag-mode-on-dblclick="false"
    :modal="false"
    :check-cross-origin="false"
    :ready="
      () => {
        onReadyCropper(this.$refs.cropper);
      }
    "
  ></VueCropper>
</template>

<script>
import VueCropper from "vue-cropperjs";
import log from "loglevel";
import ua from "../../../lib/useragent.js";
import { loadImageWithOrientationCorrection } from "../../../lib/image";

export default {
  name: "vc-share-comic-cropper",
  components: { VueCropper },
  props: {
    imageUrl: {
      type: String,
      default: ""
    },
    onReadyCropperCallback: {
      type: Function,
      default: () => {}
    },
    onErrorCropperCallback: {
      type: Function,
      default: () => {}
    },
    minContainerWidth: {
      type: Number,
      default: 200
    },
    minContainerHeight: {
      type: Number,
      default: 300
    },
    selectThumbnailImageDialog: {
      type: Boolean,
      default: false
    },
    aspectRatio: {
      type: Number,
      default: 1 / Math.sqrt(2)
    },
    imgStyle: {
      type: Object,
      default: () => ({ "max-width": "100%", height: "300px" })
    },
    cropperMargin: {
      type: Number,
      default: 20
    }
  },
  data: () => ({
    croppingImageData: {}
  }),
  watch: {
    imageUrl(newVal) {
      this.setCanvasImage(newVal);
    },
    selectThumbnailImageDialog() {
      if (this.selectThumbnailImageDialog) {
        this.$refs.cropper.replace(this.croppingImageData);
      }
    }
  },
  mounted() {
    let thisObj = this;
    if (this.imageUrl != "") {
      this.setCanvasImage(this.imageUrl);
    }
    window.addEventListener("resize", function() {
      // イベントハンドラの実行が完了し、ドキュメントの状態更新が終了した時点でCropperのリサイズ処理を行う
      if (thisObj.selectThumbnailImageDialog) {
        setTimeout(function() {
          thisObj.updateCropBoxData(thisObj.$refs.cropper);
        }, 0);
      }
    });
  },
  methods: {
    setCanvasImage(imageUrl) {
      log.debug("setCanvasImage imageUrl:", imageUrl);

      // imageUrl が設定された場合、cropperに画像を設定する。
      // cropper.js のオプション check-cross-origin = true に設定するとURLの末尾にtimestampが付与され、
      // S3の署名付きURLの場合、取得に失敗する。
      // そのため、下記で画像を読み込み、cropperに設定する。
      const image = new Image();

      image.crossOrigin = "anonymous";
      image.src = imageUrl;
      image.onload = () => {
        log.debug("image onload:", image);

        let canvasWidth = image.naturalWidth;
        let canvasHeight = image.naturalHeight;

        // canvas領域が規定値より大きい場合、規定値に合わせて縮小する
        // ※ iOS でのcanvas領域制限 : 16777216
        const maxCanvasArea = 16777210;
        if (canvasWidth * canvasHeight > maxCanvasArea) {
          let aspectRatio = canvasHeight / canvasWidth;

          canvasWidth = Math.sqrt(maxCanvasArea / aspectRatio);
          canvasHeight = canvasWidth * aspectRatio;
        }

        if (ua.getBrowser().name === "IE") {
          // IEではOrientationの補正が期待動作しないため、補正しない画像をそのまま使用する
          const canvas = document.createElement("canvas");
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
          const context = canvas.getContext("2d");
          context.drawImage(image, 0, 0, canvasWidth, canvasHeight);
          const dataUrl = canvas.toDataURL();
          this.croppingImageData = dataUrl;
          this.$refs.cropper.replace(dataUrl);
        } else {
          let options = {};
          options.maxWidth = canvasWidth;
          options.maxHeight = canvasHeight;
          loadImageWithOrientationCorrection(imageUrl, options).then(canvas => {
            this.croppingImageData = canvas.toDataURL();
            this.$refs.cropper.replace(canvas.toDataURL());
          });
        }
      };

      image.onerror = () => {
        log.error("image onerror:", image);

        this.onErrorCropperCallback();
      };
    },
    async onReadyCropper(cropper) {
      const margin = this.cropperMargin;
      const cropAspectRatio = this.aspectRatio;
      cropper.setAspectRatio(cropAspectRatio);

      const sleep = msec => new Promise(resolve => setTimeout(resolve, msec));

      let containerData = null;
      for (let i = 0; i < 10; i++) {
        containerData = cropper.getContainerData();
        if (containerData != null) {
          break;
        }
        await sleep(1000);
      }

      if (containerData == null) {
        throw "container data is empty";
      }

      const imageData = cropper.getImageData();

      let cropDataWidth = containerData.width - margin * 2;
      let cropDataHeight = cropDataWidth / cropAspectRatio;

      if (cropDataHeight > containerData.height - margin * 2) {
        cropDataHeight = containerData.height - margin * 2;
        cropDataWidth = cropDataHeight * cropAspectRatio;
      }

      let canvasWidth = cropDataWidth;
      let canvasHeight = canvasWidth / imageData.aspectRatio;

      if (canvasHeight > cropDataHeight) {
        cropper.setCanvasData({
          left: (containerData.width - canvasWidth) / 2,
          top: margin,
          width: canvasWidth
        });
      } else {
        canvasHeight = cropDataHeight;
        canvasWidth = canvasHeight * imageData.aspectRatio;
        cropper.setCanvasData({
          left: (containerData.width - canvasWidth) / 2,
          top: margin,
          height: canvasHeight
        });
      }

      cropper.setCropBoxData({
        left: (containerData.width - cropDataWidth) / 2,
        top: (containerData.height - cropDataHeight) / 2,
        width: cropDataWidth,
        height: cropDataHeight
      });

      this.onReadyCropperCallback(cropper, this.croppedCanvasToBlob);
    },
    croppedCanvasToBlob() {
      const canvas = this.$refs.cropper.getCroppedCanvas();

      return new Promise(resolve => {
        if (typeof canvas.toBlob == "function") {
          canvas.toBlob(blob => {
            resolve(blob);
          });
        } else {
          const binStr = atob(canvas.toDataURL().split(",")[1]);
          const len = binStr.length;
          const arr = new Uint8Array(len);

          for (let i = 0; i < len; i++) {
            arr[i] = binStr.charCodeAt(i);
          }

          resolve(new Blob([arr]));
        }
      });
    },
    updateCropBoxData(cropper) {
      const margin = 20;
      const cropAspectRatio = this.aspectRatio;

      const sleep = msec => new Promise(resolve => setTimeout(resolve, msec));

      let containerData = null;
      for (let i = 0; i < 10; i++) {
        containerData = cropper.getContainerData();
        if (containerData != null) {
          break;
        }
        sleep(1000);
      }

      let cropDataWidth = containerData.width - margin * 2;
      let cropDataHeight = cropDataWidth / cropAspectRatio;

      if (cropDataHeight > containerData.height - margin * 2) {
        cropDataHeight = containerData.height - margin * 2;
        cropDataWidth = cropDataHeight * cropAspectRatio;
      }

      cropper.setCropBoxData({
        left: (containerData.width - cropDataWidth) / 2,
        top: (containerData.height - cropDataHeight) / 2,
        width: cropDataWidth,
        height: cropDataHeight
      });
    }
  }
};
</script>
